import { withTranslation } from '@application/i18n/with_translation';
import { UniversalConfig } from '@application/universal_config.service';
import type { ConversationMessage } from '@care-companion/client';
import {
	type CareCompanionMessagePart,
	type ComponentEnvelope,
	decodeComponentEnvelope,
	decodeMessagePart,
} from '@care-companion/protocol';
import { Icon, IconButton, InputTextArea, Spinner, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { PageWidthLimiter } from '@component/page_width_limiter.component';
import { useResolve } from '@visiba/instantiation';
import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { CareCompanionAssistantMessage, getCitationNumbers } from './care_companion_assistant_message.component';
import { clearStoredSessionId, connectCareCompanion, endCareCompanionSession, storeSessionId } from './care_companion_client';
import { CareCompanionMessageComponent } from './care_companion_message_component.component';
import { getCareCompanionMessageId, RetriableCareCompanionMessage } from './care_companion_message_retry';

interface ViewCareCompanionProps {
	onClose?: () => void;
	sessionId?: string;
}

export function ViewCareCompanion({ onClose, sessionId: sessionIdProp }: ViewCareCompanionProps): JSX.Element {
	if (sessionIdProp != null) {
		return <CareCompanionChat sessionId={sessionIdProp} onClose={onClose} />;
	}

	return <RoutedCareCompanionChat onClose={onClose} />;
}

function RoutedCareCompanionChat({ onClose }: Pick<ViewCareCompanionProps, 'onClose'>): JSX.Element {
	const routeParams = useParams<{ sessionId: string }>();

	return <CareCompanionChat sessionId={routeParams.sessionId} onClose={onClose} />;
}

function CareCompanionChat({ onClose = () => window.history.back(), sessionId }: ViewCareCompanionProps): JSX.Element {
	const universalConfig = useResolve(UniversalConfig);
	const [messages, setMessages] = useState<ConversationMessage[]>([]);
	const [message, setMessage] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [isSending, setIsSending] = useState(false);
	const [isClosing, setIsClosing] = useState(false);
	const [hasError, setHasError] = useState(false);
	const messageList = useRef<HTMLDivElement>(null);
	const retryMessage = useRef<RetriableCareCompanionMessage | null>(null);

	useEffect(() => {
		let isMounted = true;

		async function loadConversation(): Promise<void> {
			if (sessionId == null) {
				setHasError(true);
				setIsLoading(false);
				return;
			}

			try {
				const connection = await connectCareCompanion(sessionId);
				const conversation = await connection.client.chats.get(connection.conversationId);

				if (isMounted) {
					storeSessionId(universalConfig.licenceHolderId, connection.sessionId);
					setMessages(conversation.messages);
				}
			} catch {
				if (isMounted) setHasError(true);
			} finally {
				if (isMounted) setIsLoading(false);
			}
		}

		void loadConversation();

		return () => {
			isMounted = false;
		};
	}, [sessionId, universalConfig.licenceHolderId]);

	useEffect(() => {
		const list = messageList.current;
		if (messages.length > 0 && list != null) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
	}, [messages]);

	async function sendMessage(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const content = message.trim();
		if (content === '' || sessionId == null || isSending) return;

		setIsSending(true);
		setHasError(false);
		setMessage('');

		const optimisticMessage: ConversationMessage = {
			id: getCareCompanionMessageId(content, retryMessage.current, () => crypto.randomUUID()),
			role: 'user',
			content,
			created: new Date().toISOString(),
		};
		setMessages((current) => [...current, optimisticMessage]);
		let streamedAssistantId: string | null = null;

		try {
			const connection = await connectCareCompanion(sessionId);
			const stream = await connection.client.messages.stream(connection.conversationId, {
				messageId: optimisticMessage.id,
				content,
			});
			let streamedContent = '';
			let streamedComponents: ComponentEnvelope[] = [];
			let streamedParts: CareCompanionMessagePart[] = [];
			let completedMessage: ConversationMessage | null = null;
			const streamedMessageCreated = new Date().toISOString();

			for await (const value of stream) {
				const part = asStreamPart(value);
				if (part == null) continue;

				if (part.type === 'start' && typeof part.messageId === 'string') {
					streamedAssistantId = part.messageId;
				} else if (part.type === 'text-delta' && typeof part.delta === 'string') {
					streamedAssistantId ??= `stream-${optimisticMessage.id}`;
					streamedContent += part.delta;
					streamedParts = appendStreamedTextPart(streamedParts, part.delta);
					const messageId = streamedAssistantId;
					setMessages((current) =>
						upsertStreamedMessage(current, {
							id: messageId,
							role: 'assistant',
							content: streamedContent,
							created: streamedMessageCreated,
							components: streamedComponents,
							parts: streamedParts,
						}),
					);
				} else if (part.type === 'data-component') {
					const component = getStreamedComponent(part.data);
					if (component == null) continue;

					streamedAssistantId ??= `stream-${optimisticMessage.id}`;
					streamedComponents = upsertStreamedComponent(streamedComponents, component);
					const messageId = streamedAssistantId;
					setMessages((current) =>
						upsertStreamedMessage(current, {
							id: messageId,
							role: 'assistant',
							content: streamedContent,
							created: streamedMessageCreated,
							components: streamedComponents,
							parts: streamedParts,
						}),
					);
				} else if (part.type === 'data-reference') {
					const messagePart = getStreamedMessagePart(part);
					if (messagePart == null) continue;

					streamedAssistantId ??= `stream-${optimisticMessage.id}`;
					streamedParts = upsertStreamedMessagePart(streamedParts, messagePart);
					const messageId = streamedAssistantId;
					setMessages((current) =>
						upsertStreamedMessage(current, {
							id: messageId,
							role: 'assistant',
							content: streamedContent,
							created: streamedMessageCreated,
							components: streamedComponents,
							parts: streamedParts,
						}),
					);
				} else if (part.type === 'data-result') {
					completedMessage = getCompletedMessage(part.data);
				} else if (part.type === 'data-error') {
					throw new Error(getStreamErrorMessage(part.data));
				} else if (part.type === 'error') {
					throw new Error(typeof part.errorText === 'string' ? part.errorText : 'Care Companion stream failed.');
				}
			}

			if (completedMessage == null) {
				throw new Error('Care Companion stream ended without a result.');
			}

			const finalMessage: ConversationMessage = {
				...completedMessage,
				parts: completedMessage.parts != null && completedMessage.parts.length > 0 ? completedMessage.parts : streamedParts,
			};
			retryMessage.current = null;
			setMessages((current) => [
				...current.filter((item) => item.id !== streamedAssistantId && item.id !== finalMessage.id),
				finalMessage,
			]);
		} catch {
			retryMessage.current = { id: optimisticMessage.id, content };
			setMessage(content);
			setMessages((current) => current.filter((item) => item.id !== optimisticMessage.id && item.id !== streamedAssistantId));
			setHasError(true);
		} finally {
			setIsSending(false);
		}
	}

	function handleComposerKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
		if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
			return;
		}

		if (event.shiftKey) return;

		event.preventDefault();
		event.currentTarget.form?.requestSubmit();
	}

	async function endSession(): Promise<void> {
		if (sessionId == null || isClosing) return;

		setIsClosing(true);
		setHasError(false);
		try {
			await endCareCompanionSession(sessionId);
			clearStoredSessionId(universalConfig.licenceHolderId);
			onClose();
		} catch {
			setHasError(true);
			setIsClosing(false);
		}
	}

	return (
		<StyledPage>
			<PageWidthLimiter>
				<StyledChat>
					<StyledHeader>
						<StyledAssistantIdentity>
							<AssistantAvatar aria-hidden='true'>
								<Icon type='speechBubbleDots' />
							</AssistantAvatar>
							<Text.h1 variant='headingMedium'>{withTranslation('CareCompanion.Chat.Title')}</Text.h1>
						</StyledAssistantIdentity>
						<IconButton
							icon='x'
							size='medium'
							variant='neutral'
							text={withTranslation('Common.Button.Close')}
							loading={isClosing}
							onClick={endSession}
						/>
					</StyledHeader>

					<StyledMessages ref={messageList} aria-live='polite'>
						{isLoading ? (
							<StyledStatus>
								<Spinner size='medium' />
								<Text variant='bodyRegular'>{withTranslation('CareCompanion.Chat.Loading')}</Text>
							</StyledStatus>
						) : (
							<>
								{messages.length === 0 && !hasError ? (
									<MessageRow data-role='assistant'>
										<MessageAvatar aria-hidden='true'>
											<Icon type='speechBubbleDots' />
										</MessageAvatar>
										<MessageBubble data-role='assistant'>{withTranslation('CareCompanion.Chat.Intro')}</MessageBubble>
									</MessageRow>
								) : null}
								{(() => {
									const visibleMessages = messages.filter((item) => item.role === 'user' || item.role === 'assistant');
									const citationNumbers = getCitationNumbers(visibleMessages);

									return visibleMessages.map((item) => (
										<MessageRow key={item.id} data-role={item.role}>
											{item.role === 'assistant' ? (
												<MessageAvatar aria-hidden='true'>
													<Icon type='speechBubbleDots' />
												</MessageAvatar>
											) : null}
											<MessageBubble data-role={item.role}>
												{item.role === 'assistant' ? (
													<CareCompanionAssistantMessage message={item} citationNumbers={citationNumbers} />
												) : (
													item.content
												)}
												{item.role === 'assistant' && item.components != null && item.components.length > 0 ? (
													<StyledComponents>
														{item.components.map((component) => (
															<CareCompanionMessageComponent key={component.id} component={component} />
														))}
													</StyledComponents>
												) : null}
											</MessageBubble>
										</MessageRow>
									));
								})()}
							</>
						)}
					</StyledMessages>

					{hasError ? (
						<StyledError role='alert'>
							<Text variant='bodySmallRegular'>{withTranslation('CareCompanion.Chat.Error')}</Text>
						</StyledError>
					) : null}

					<StyledComposer onSubmit={sendMessage}>
						<InputTextArea
							hideLabel
							label={withTranslation('CareCompanion.Chat.MessageLabel')}
							placeholder={withTranslation('CareCompanion.Chat.MessagePlaceholder')}
							value={message}
							onChange={(event) => setMessage(event.target.value)}
							onKeyDown={handleComposerKeyDown}
							disabled={isSending || isClosing}
						/>
						<IconButton
							type='submit'
							icon='paperPlaneFilled'
							size='large'
							variant='interactive'
							text={withTranslation('CareCompanion.Chat.Send')}
							loading={isSending}
							disabled={message.trim() === '' || isClosing}
						/>
					</StyledComposer>
				</StyledChat>
			</PageWidthLimiter>
		</StyledPage>
	);
}

type StreamPart = Record<string, unknown> & { type: string };

function asStreamPart(value: unknown): StreamPart | null {
	if (value == null || typeof value !== 'object' || !('type' in value) || typeof value.type !== 'string') {
		return null;
	}

	return value as StreamPart;
}

function getCompletedMessage(value: unknown): ConversationMessage | null {
	if (value == null || typeof value !== 'object' || !('message' in value)) return null;

	const message = value.message;
	if (
		message == null ||
		typeof message !== 'object' ||
		!('id' in message) ||
		typeof message.id !== 'string' ||
		!('role' in message) ||
		message.role !== 'assistant' ||
		!('content' in message) ||
		typeof message.content !== 'string' ||
		!('created' in message) ||
		typeof message.created !== 'string'
	) {
		return null;
	}

	return message as ConversationMessage;
}

function getStreamedComponent(value: unknown): ComponentEnvelope | null {
	const result = decodeComponentEnvelope(value);
	return result.valid ? result.component : null;
}

function getStreamedMessagePart(part: StreamPart): CareCompanionMessagePart | null {
	const result = decodeMessagePart({
		type: 'reference',
		id: typeof part.id === 'string' ? part.id : crypto.randomUUID(),
		reference: part.data,
	});
	return result.valid ? result.part : null;
}

function getStreamErrorMessage(value: unknown): string {
	if (value != null && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
		return value.message;
	}

	return 'Care Companion stream failed.';
}

function upsertStreamedMessage(messages: ConversationMessage[], streamedMessage: ConversationMessage): ConversationMessage[] {
	const existingIndex = messages.findIndex((item) => item.id === streamedMessage.id);
	if (existingIndex < 0) return [...messages, streamedMessage];

	return messages.map((item, index) => (index === existingIndex ? streamedMessage : item));
}

function upsertStreamedComponent(components: ComponentEnvelope[], component: ComponentEnvelope): ComponentEnvelope[] {
	const index = components.findIndex((item) => item.id === component.id);
	if (index < 0) return [...components, component];

	return components.map((item, itemIndex) => (itemIndex === index ? component : item));
}

function upsertStreamedMessagePart(parts: CareCompanionMessagePart[], part: CareCompanionMessagePart): CareCompanionMessagePart[] {
	if (part.type !== 'reference') return [...parts, part];

	const index = parts.findIndex((item) => item.type === 'reference' && item.id === part.id);
	if (index < 0) return [...parts, part];

	return parts.map((item, itemIndex) => (itemIndex === index ? part : item));
}

function appendStreamedTextPart(parts: CareCompanionMessagePart[], text: string): CareCompanionMessagePart[] {
	const lastPart = parts.at(-1);
	if (lastPart?.type !== 'text') return [...parts, { type: 'text', text }];

	return [...parts.slice(0, -1), { ...lastPart, text: lastPart.text + text }];
}

const StyledPage = styled.main`
	min-height: calc(100vh - var(--header-height));
	padding: ${tokens.spacing.x2};
	background: ${tokens.color.bg.page};
`;

const StyledChat = styled.section`
	display: flex;
	flex-direction: column;
	height: min(760px, calc(100dvh - var(--header-height) - ${tokens.spacing.x4}));
	min-height: min(520px, calc(100dvh - var(--header-height) - ${tokens.spacing.x4}));
	width: 800px;
	max-width: 100%;
	margin: 0 auto;
	overflow: hidden;
	background: ${tokens.color.bg.surface};
	border: 1px solid ${tokens.color.border.muted};
	border-radius: ${tokens.borderRadius.large};
	box-shadow: 0 12px 40px rgb(0 0 0 / 8%);
`;

const StyledHeader = styled.header`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.spacing.x2};
	padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
	background: ${tokens.color.bg.surface};
	border-bottom: 1px solid ${tokens.color.border.muted};
`;

const StyledAssistantIdentity = styled.div`
	display: flex;
	align-items: center;
	gap: ${tokens.spacing.x1_5};
`;

const AssistantAvatar = styled.div`
	display: grid;
	place-items: center;
	width: 40px;
	height: 40px;
	color: ${tokens.color.content.default};
	background: ${tokens.color.bg.surfaceBrand};
	border-radius: 50%;
`;

const StyledStatus = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: ${tokens.spacing.x1};
	min-height: 100%;
`;

const StyledMessages = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	gap: ${tokens.spacing.x2};
	min-height: 0;
	overflow-y: auto;
	padding: ${tokens.spacing.x3};
	background: ${tokens.color.bg.page};
`;

const MessageRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${tokens.spacing.x1};

	&[data-role='user'] {
		justify-content: flex-end;
	}
`;

const MessageAvatar = styled.div`
	display: grid;
	flex: 0 0 auto;
	place-items: center;
	width: 28px;
	height: 28px;
	color: ${tokens.color.content.default};
	background: ${tokens.color.bg.surfaceMuted};
	border-radius: 50%;
`;

const MessageBubble = styled.div`
	max-width: min(78%, 600px);
	padding: ${tokens.spacing.x1_5} ${tokens.spacing.x2};
	border-radius: ${tokens.borderRadius.large};
	font: ${tokens.text.body.regular};
	line-height: 1.5;
	white-space: pre-wrap;
	overflow-wrap: anywhere;

	&[data-role='assistant'] {
		background: ${tokens.color.bg.surface};
		border: 1px solid ${tokens.color.border.muted};
		border-bottom-left-radius: ${tokens.borderRadius.small};
		color: ${tokens.color.content.default};
	}

	&[data-role='user'] {
		background: ${tokens.color.bg.surfaceBrand};
		border-bottom-right-radius: ${tokens.borderRadius.small};
		color: ${tokens.color.content.default};
	}
`;

const StyledComponents = styled.div`
	display: grid;
	gap: ${tokens.spacing.x1};
	margin-top: ${tokens.spacing.x1_5};
	white-space: normal;
`;

const StyledError = styled.div`
	padding: ${tokens.spacing.x1} ${tokens.spacing.x2};
	color: ${tokens.color.danger.content};
	background: ${tokens.color.bg.surface};
`;

const StyledComposer = styled.form`
	display: flex;
	align-items: flex-end;
	gap: ${tokens.spacing.x1};
	padding: ${tokens.spacing.x2};
	background: ${tokens.color.bg.surface};
	border-top: 1px solid ${tokens.color.border.muted};

	& > :first-child {
		flex: 1 1 auto;
	}
`;
